import { IUnauthenticatedRealmSettings } from '@src/types/realmSettings.types'
import { PrivilegedUserRoles } from '@src/types/user.types'
import { isVSMSetupPageEnabled } from '@src/utilities/vsm'
import { RouteRecordRaw } from 'vue-router'

interface IRouteWithId {
  params: { id: string }
}

interface IRouteWithQuery {
  query?: string
}

interface IRouteWithLang {
  params: { lang: string }
}

export default function getAdminRouteConfig(realmSettings?: IUnauthenticatedRealmSettings) : RouteRecordRaw[] {
  return [
    {
      path: 'content',
      meta: {
        requiredRole: PrivilegedUserRoles.ContentAdmin
      },
      children: [
        {
          path: 'questions',
          name: 'admin/content/questions',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminQuestions/components/AdminQuestionsTable.vue'),
          props: (route: IRouteWithQuery) => ({ query: route.query || undefined })
        },
        {
          path: 'questions',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
          children: [
            {
              path: 'import',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminQuestions/components/AdminQuestionsImport.vue')
            },
            {
              path: 'export',
              meta: {
                requiredRole: PrivilegedUserRoles.SuperAdmin
              },
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminQuestions/components/AdminQuestionsExport.vue')
            },
            {
              path: ':id(\\d+)/view',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminQuestions/components/AdminQuestionObjectView.vue'),
              props: (route: IRouteWithId) => ({ questionId: parseInt(route.params.id, 10) })
            },
            {
              path: ':id(\\d+)/edit',
              name: 'admin/content/questions/edit',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminQuestions/components/AdminQuestionEdit.vue'),
              props: (route: IRouteWithId) => ({ questionId: parseInt(route.params.id, 10) })
            }
          ]
        },
        {
          path: 'solutions',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
          children: [
            {
              path: ':id(\\d+)/edit',
              name: 'admin/content/solutions/edit',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminQuestions/components/AdminSolutionEdit.vue'),
              props: (route: IRouteWithId) => ({ solutionId: parseInt(route.params.id, 10) })
            }
          ]
        },
        {
          path: 'comments',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
          children: [
            {
              path: ':id(\\d+)/edit',
              name: 'admin/content/comments/edit',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminQuestions/components/AdminCommentEdit.vue'),
              props: (route: IRouteWithId) => ({ commentId: parseInt(route.params.id, 10) })
            }
          ]
        },
        {
          path: 'knowledge-spaces',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
          redirect() {
            if (realmSettings?.knowledge_spaces.knowledge_spaces_in_pricing_plan) {
              return 'knowledge-spaces'
            }
            return '/error/403'
          },
          children: [
            {
              path: '',
              name: 'admin/content/knowledge-spaces/overview',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminKnowledgeSpaces/components/AdminKnowledgeSpacesOverview.vue')
            },
            {
              path: ':id(\\d+)',
              name: 'admin/content/knowledge-spaces/edit',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminKnowledgeSpaces/components/AdminKnowledgeSpacesEdit.vue'),
              props: (route: IRouteWithId) => ({ spaceId: parseInt(route.params.id, 10) || undefined })
            },
            {
              path: 'new',
              name: 'admin/content/knowledge-spaces/new',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminKnowledgeSpaces/components/AdminKnowledgeSpacesEdit.vue'),
              props: () => ({ spaceId: undefined })
            }
          ]
        },
        {
          path: 'documents',
          name: 'admin/content/documents',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminDocuments/index.vue')
        },
        {
          path: 'categories',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
          redirect() {
            if (realmSettings?.categories.categories_in_pricing_plan) {
              return 'categories'
            }
            return '/error/403'
          },
          children: [
            {
              path: '',
              name: 'admin/content/categories/overview',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminCategories/components/AdminCategoriesOverview.vue')
            },
            {
              path: ':id(\\d+)',
              name: 'admin/content/categories/edit',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminCategories/components/AdminCategoriesEdit.vue'),
              props: (route: IRouteWithId) => ({ categoryId: parseInt(route.params.id, 10) || undefined })
            },
            {
              path: 'new',
              name: 'admin/content/categories/new',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminCategories/components/AdminCategoriesEdit.vue'),
              props: () => ({ categoryId: undefined })
            }
          ]
        },
        {
          path: 'topics',
          name: 'admin/topics/overview',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminTopics/index.vue')
        },
        {
          path: 'topics',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
          children: [
            {
              path: 'new',
              name: 'admin/content/topics/new',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminTopics/components/CreateTopic.vue')
            },
            {
              // Note: this route should always be above ':id', because otherwise it will match the
              // edit page using "export" as the id
              path: 'export',
              name: 'admin/content/topics/export',
              meta: {
                // requiredRole: PrivilegedUserRoles.SuperAdmin
              },
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminTopics/components/AdminTopicsExport.vue')
            },
            {
              path: 'import',
              name: 'admin/content/topics/import',
              meta: {
                requiredRole: PrivilegedUserRoles.ContentAdmin
              },
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminTopicsImport/index.vue')
            },
            {
              // Note: must stay above ':id' so the edit route does not capture "duplicates".
              // Hidden internal tool — no entry point in the UI, reachable only via direct URL.
              path: 'duplicates',
              name: 'admin/content/topics/duplicates',
              meta: {
                requiredRole: PrivilegedUserRoles.ContentAdmin
              },
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminTopicsDuplicates/index.vue')
            },
            {
              // STARDEV-11361 port from fail-fast; temporary route so admins can test.
              // Must stay above ':id' so the edit route does not capture the path.
              // Hidden internal tool — no entry point in the UI, reachable only via direct URL.
              path: 'user-concept-import',
              name: 'admin/content/topics/user-concept-import',
              meta: {
                requiredRole: PrivilegedUserRoles.ContentAdmin
              },
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminUserConceptImport/index.vue')
            },
            {
              path: ':id',
              name: 'admin/content/topics/edit',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminTopics/components/AdminTopicsEdit.vue')
            },
            {
              path: 'merge/:id',
              name: 'admin/content/topics/merge',
              component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminTopicsMerge/index.vue'),
              meta: {
                requiredRole: PrivilegedUserRoles.ContentAdmin
              }
            }
          ]
        }
      ]
    },
    {
      path: 'gamification',
      component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
      children: [
        {
          path: 'export',
          meta: {
            requiredRole: PrivilegedUserRoles.UserStatisticsAdmin
          },
          name: 'admin/gamification/export',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminGamificationExport/index.vue'),
          beforeEnter: (to, from, next) => {
            if (realmSettings?.gamification.user_score_enabled) {
              next()
            } else {
              next('/error/403')
            }
          }
        }
      ]
    },
    {
      // Hidden internal tool — no entry point in the UI, reachable only via direct URL.
      // Superadmin only; deliberately NOT under 'content' so it does not inherit ContentAdmin.
      path: 'question-routing',
      name: 'admin/question-routing',
      meta: {
        requiredRole: PrivilegedUserRoles.SuperAdmin
      },
      component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminQuestionRouting/index.vue')
    },
    {
      path: 'users',
      component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
      meta: {
        requiredRole: PrivilegedUserRoles.UserAdmin
      },
      children: [
        {
          path: '',
          name: 'admin/users/table',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminUsers/components/AdminUsersTable.vue'),
          props: (route: IRouteWithQuery) => ({ query: route.query || undefined })
        },
        {
          path: ':id(\\d+)',
          name: 'admin/users/edit',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminUsers/components/AdminUserEdit.vue'),
          props: (route: IRouteWithId) => ({ userId: parseInt(route.params.id, 10) })
        },
        {
          path: 'import',
          name: 'admin/users/import',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminUsers/components/AdminUserImport.vue')
        },
        {
          path: 'export',
          name: 'admin/users/export',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminUsers/components/AdminUserExport.vue')
        },
        {
          path: 'new',
          name: 'admin/users/new',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminUsers/components/AdminUserNew.vue')
        }
      ]
    },
    {
      path: 'guidelines',
      meta: {
        requiredRole: PrivilegedUserRoles.CommunicationAdmin
      },
      component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
      children: [
        {
          path: '',
          name: 'admin/guidelines/overview',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminGuidelines/components/AdminGuidelinesOverview.vue')
        },
        {
          path: ':lang',
          name: 'admin/guidelines/edit',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminGuidelines/components/AdminGuidelinesEdit.vue'),
          props: (route: IRouteWithLang) => ({ languageId: route.params.lang })
        }
      ]
    },
    {
      path: 'context-message',
      meta: {
        requiredRole: PrivilegedUserRoles.CommunicationAdmin
      },
      component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
      children: [
        {
          path: '',
          name: 'admin/context-message',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/ContextMessage/components/AdminContextMessage.vue')
        },
        {
          path: ':lang',
          name: 'admin/context-message/language',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/ContextMessage/components/AdminContextMessageEditLanguage.vue'),
          props: (route: IRouteWithLang) => ({ languageId: route.params.lang })
        }
      ]
    },
    {
      path: 'announcements',
      meta: {
        requiredRole: PrivilegedUserRoles.CommunicationAdmin
      },
      component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
      children: [
        {
          path: '',
          name: 'admin/announcements/overview',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminAnnouncement/AdminAnnouncementOverview.vue')
        },
        {
          path: ':id(\\d+)',
          name: 'admin/announcements/edit',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminAnnouncement/AdminAnnouncementEdit.vue'),
          props: (route: IRouteWithId) => ({ announcementId: parseInt(route.params.id, 10) })
        },
        {
          path: 'new',
          name: 'admin/announcements/new',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminAnnouncement/AdminAnnouncementEdit.vue'),
          props: () => ({ announcementId: undefined })
        }
      ]
    },
    {
      path: 'settings',
      name: 'admin/settings',
      meta: {
        // Realm settings service enforces UserAdmin role, not SettingsAdmin
        // https://github.com/Starmind/realm-settings-service/blob/develop/app/src/main/scala/com/starmind/rss/settings/SettingsRoute.scala#L260
        requiredRole: PrivilegedUserRoles.UserAdmin
      },
      component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
      children: [
        {
          path: 'algorithm',
          name: 'admin/settings/algorithm',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminAlgorithmSettings.vue')
        },
        {
          path: 'auth',
          name: 'admin/settings/auth',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminAuthSettings.vue')
        },
        {
          path: 'workspace',
          name: 'admin/settings/workspace',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminWorkspaceSettings.vue')
        },
        {
          path: 'content',
          name: 'admin/settings/content',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminContentSettings.vue')
        },
        {
          path: 'mail',
          name: 'admin/settings/mail',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminEmailSettings.vue')
        },
        {
          path: 'gamification',
          alias: 'user-score',
          name: 'admin/settings/gamification',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminGamificationSettings.vue')
        },
        {
          path: 'solution-validation',
          name: 'admin/settings/solution-validation',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminSolutionValidationSettings.vue')
        },
        {
          path: 'solution-value',
          name: 'admin/settings/solution-value',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminSolutionValueSettings.vue')
        },
        {
          path: 'question-escalation',
          name: 'admin/settings/question-escalation',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminQuestionEscalationSettings.vue')
        },
        {
          path: 'flagging',
          name: 'admin/settings/flagging',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminQuestionFlaggingSettings.vue')
        },
        {
          path: 'kpi',
          name: 'admin/settings/kpi',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminKpiSettings.vue')
        },
        {
          path: 'open-search',
          name: 'admin/settings/open-search',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminOpenSearchSettings.vue')
        },
        {
          path: 'microsoft',
          name: 'admin/settings/microsoft',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminMicrosoftSettings.vue')
        },
        {
          path: 'rss',
          name: 'admin/settings/rss',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminRssSettings.vue')
        },
        {
          path: 'web-hook',
          name: 'admin/settings/web-hook',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminWebHooksSettings.vue')
        },
        {
          path: 'network',
          name: 'admin/settings/network',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminNetworkSettings.vue')
        },
        {
          path: 'user',
          name: 'admin/settings/user',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminUserSettings.vue')
        },
        {
          path: 'connectors',
          name: 'admin/settings/connectors',
          component: () => import(/* webpackChunkName: "admin" */'@src/views/Admin/DataSources/index.vue')
        },
        {
          path: 'approval-message/',
          name: 'admin/settings/approval-message',
          component: () => import(/* webpackChunkName: "admin" */'@src/views/AdminTerms/components/AdminTermsOverview.vue')
        },
        {
          path: 'approval-message/:id(\\d+)',
          name: 'admin/settings/approval-message/edit',
          component: () => import(/* webpackChunkName: "admin" */'@src/views/AdminTerms/components/AdminTermsEdit.vue'),
          props: (route: IRouteWithId) => ({ version: Number(route.params.id) })
        },
        {
          path: 'approval-message/:id(\\d+)/:lang',
          name: 'admin/settings/approval-message/translation',
          component: () => import(/* webpackChunkName: "admin" */'@src/views/AdminTerms/components/AdminTermsTranslation.vue'),
          props: (route: IRouteWithLang & IRouteWithId) => ({ version: Number(route.params.id), language: route.params.lang })
        },
        {
          path: 'approval-group',
          redirect() {
            if (realmSettings?.solution_authority.solution_authority_in_pricing_plan) {
              return 'approval-group'
            }
            return '/error/403'
          },
          children: [
            {
              path: '',
              name: 'admin/settings/approval-group',
              component: () => import(/* webpackChunkName: "admin" */'@src/views/Admin/SolutionApproval/components/SolutionApprovalOverview.vue')
            },
            {
              path: ':id',
              name: 'admin/settings/approval-group/id',
              component: () => import(/* webpackChunkName: "admin" */'@src/views/Admin/SolutionApproval/components/SolutionApprovalDetail.vue'),
              props: (route: IRouteWithId) => ({ id: Number(route.params.id) || undefined })
            }
          ]
        },
        {
          path: 'translations',
          name: 'admin/settings/translations',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminSettings/AdminTranslationSettings.vue'),
          beforeEnter: (to, from, next) => {
            if (realmSettings?.translation.translations_rolled_out) {
              next()
            } else {
              next('/error/403')
            }
          }
        }
      ]
    },
    {
      path: 'vsm',
      name: 'admin/vsm',
      meta: {
        requiredRole: PrivilegedUserRoles.NetworkStatisticsAdmin
      },
      component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
      redirect() {
        if (realmSettings?.vsm.vsm_enabled) {
          return 'vsm'
        }
        return '/error/403'
      },
      children: [
        {
          path: 'setup',
          name: 'admin/vsm/setup',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/VSM/setupPage/index.vue'),
          beforeEnter: (to, from, next) => {
            if (isVSMSetupPageEnabled(realmSettings?.metadata.go_live_date)) {
              next()
            } else {
              next('/admin/vsm/metrics')
            }
          }
        },
        {
          path: 'metrics',
          name: 'admin/vsm/metrics',
          component: () => import(/* webpackChunkName: "admin" */'@src/views/VSM/metricsPage/index.vue')
        }
      ]
    },
    {
      path: 'statistics/kpi',
      component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/AdminContainer.vue'),
      children: [
        {
          path: '',
          meta: {
            requiredRole: PrivilegedUserRoles.NetworkStatisticsAdmin
          },
          name: 'admin/kpi/export',
          component: () => import(/* webpackChunkName: "admin" */ '@src/views/AdminKpiExport/index.vue')
        }
      ]
    },
    {
      path: 'statistics/dashboard',
      meta: {
        requiredRole: PrivilegedUserRoles.NetworkStatisticsAdmin
      },
      component: () => import(/* webpackChunkName: "admin" */'@src/views/AdminAnalyticsDashboard/index.vue'),
      // TODO: check analytics dashboard enabled
      children: [
        {
          path: '',
          alias: 'overview',
          name: 'analytics-dashboard/overview',
          component: () => import(/* webpackChunkName: "admin" */'@src/views/AdminAnalyticsDashboard/AdminAnalyticsDashboardOverviewMetrics.vue')
        },
        {
          path: 'content',
          name: 'analytics-dashboard/content',
          component: () => import(/* webpackChunkName: "admin" */'@src/views/AdminAnalyticsDashboard/AdminAnalyticsDashboardContentMetrics.vue')
        },
        {
          path: 'views',
          name: 'analytics-dashboard/views',
          component: () => import(/* webpackChunkName: "admin" */'@src/views/AdminAnalyticsDashboard/AdminAnalyticsDashboardViewMetrics.vue')
        },
        {
          path: 'collaboration',
          name: 'analytics-dashboard/collaboration',
          component: () => import(/* webpackChunkName: "admin" */'@src/views/AdminAnalyticsDashboard/AdminAnalyticsDashboardCollaborationMetrics.vue')
        },
        {
          path: 'network-health',
          name: 'analytics-dashboard/network-health',
          component: () => import(/* webpackChunkName: "admin" */'@src/views/AdminAnalyticsDashboard/AdminAnalyticsDashboardHealthMetrics.vue')
        },
        {
          path: 'user',
          name: 'analytics-dashboard/user',
          component: () => import(/* webpackChunkName: "admin" */'@src/views/AdminAnalyticsDashboard/AdminAnalyticsDashboardUserMetrics.vue')
        }
      ]
    },
    {
      path: 'feature-requests',
      alias: 'feature-requests/*',
      name: 'feature-requests',
      component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/FeatureRequests/index.vue'),
      beforeEnter: (to, from, next) => {
        if (realmSettings?.canny.enabled) {
          next()
        } else {
          next('/error/403')
        }
      }
    },
    {
      path: 'need-help',
      name: 'admin/need-help',
      component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/NeedHelp/index.vue')
    }
  ]
}
